/**
 * Redux reducer for the Review (Contract Review Against Firm Standards) feature.
 *
 * Registered in the store as "review".
 */

import { AnyAction } from 'redux';
import { ReviewState, Finding, ExtractionProgress, ReviewCollaborator, SignOffStatus } from '../../../types/review';
import {
  REVIEW_LOADING,
  REVIEW_FETCH_LIST,
  REVIEW_APPEND_LIST,
  REVIEW_SET_DETAIL,
  REVIEW_LOADING_DETAIL,
  REVIEW_UPDATE_IN_LIST,
  REVIEW_REMOVE_FROM_LIST,
  REVIEW_SET_SESSION,
  REVIEW_LOADING_SESSION,
  REVIEW_SET_FINDINGS,
  REVIEW_APPEND_FINDINGS,
  REVIEW_LOADING_FINDINGS,
  REVIEW_SET_UPLOADING,
  REVIEW_EXTRACTING_PLAYBOOK,
  REVIEW_ERROR,
  REVIEW_FINDING_RECEIVED,
  REVIEW_DOCUMENT_COMPLETED,
  REVIEW_REVIEW_COMPLETED,
  REVIEW_SESSION_PROGRESS,
  REVIEW_LOADING_SESSIONS,
  REVIEW_FETCH_SESSIONS,
  REVIEW_APPEND_SESSIONS,
  REVIEW_REMOVE_SESSION_FROM_LIST,
  REVIEW_BULK_REMOVE_SESSIONS,
  REVIEW_RENAME_SESSION_IN_LIST,
  REVIEW_RESTORE_SESSION,
  REVIEW_EXTRACT_CONNECTING,
  REVIEW_EXTRACT_CONNECTED,
  REVIEW_EXTRACT_STEPS,
  REVIEW_EXTRACT_STEP,
  REVIEW_EXTRACT_COMPLETED,
  REVIEW_EXTRACT_FAILED,
  REVIEW_EXTRACT_RESET,
  REVIEW_SET_COLLABORATORS,
  REVIEW_LOADING_COLLABORATORS,
  REVIEW_ADD_COLLABORATOR,
  REVIEW_UPDATE_COLLABORATOR,
  REVIEW_REMOVE_COLLABORATOR,
  REVIEW_SET_AUDIT_LOG,
  REVIEW_APPEND_AUDIT_LOG,
  REVIEW_LOADING_AUDIT_LOG,
  REVIEW_SET_DOCUMENT_CONTENT,
  REVIEW_LOADING_DOCUMENT,
  REVIEW_FINDING_DECISION_RECEIVED,
  REVIEW_SET_DECISION_SUMMARY,
  REVIEW_UPDATE_FINDING,
  REVIEW_ADD_FINDING_NOTE,
  REVIEW_UPDATE_FINDING_NOTE,
  REVIEW_DELETE_FINDING_NOTE,
  REVIEW_BULK_UPDATE_FINDINGS,
  REVIEW_SET_SIGN_OFF_STATUS,
  REVIEW_LOADING_SIGN_OFF,
  REVIEW_SESSION_STATUS_CHANGE,
  REVIEW_BULK_REMOVE_PLAYBOOKS,
} from '../../actions/Review/ReviewActions';

const initialExtractionProgress: ExtractionProgress = {
  isConnecting: false,
  connected: false,
  steps: [],
  complete: false,
  ok: false,
  summary: null,
};

const initialState: ReviewState = {
  items: [],
  totalItems: 0,
  currentPage: 1,
  totalPages: 1,
  isLoading: false,

  sessionItems: [],
  sessionTotalItems: 0,
  sessionCurrentPage: 1,
  sessionTotalPages: 1,
  isLoadingSessions: false,

  currentReview: null,
  isLoadingDetail: false,

  currentSession: null,
  isLoadingSession: false,

  findings: [],
  findingsTotal: 0,
  findingsPage: 1,
  isLoadingFindings: false,
  decisionSummary: null,

  collaborators: [],
  isLoadingCollaborators: false,

  auditLog: [],
  auditLogTotal: 0,
  auditLogPage: 1,
  isLoadingAuditLog: false,

  documentContent: null,
  isLoadingDocument: false,

  signOffStatus: null,
  isLoadingSignOff: false,

  isUploading: false,
  isExtractingPlaybook: false,

  extractionProgress: initialExtractionProgress,

  error: null,
};

const reviewReducer = (
  state = initialState,
  action: AnyAction,
): ReviewState => {
  switch (action.type) {
    // ─── List loading ───────────────────────────────
    case REVIEW_LOADING:
      return { ...state, isLoading: action.payload, error: null };

    case REVIEW_FETCH_LIST:
      return {
        ...state,
        items: action.payload.items,
        totalItems: action.payload.total,
        currentPage: action.payload.page,
        totalPages: action.payload.total_pages,
      };

    case REVIEW_APPEND_LIST:
      return {
        ...state,
        items: [...state.items, ...action.payload.items],
        totalItems: action.payload.total,
        currentPage: action.payload.page,
        totalPages: action.payload.total_pages,
      };

    // ─── Detail ─────────────────────────────────────
    case REVIEW_SET_DETAIL:
      return { ...state, currentReview: action.payload };

    case REVIEW_LOADING_DETAIL:
      return { ...state, isLoadingDetail: action.payload, error: null };

    // ─── List mutations ─────────────────────────────
    case REVIEW_UPDATE_IN_LIST:
      return {
        ...state,
        items: state.items.map((item) =>
          item.uuid === action.payload.uuid ? { ...item, ...action.payload } : item,
        ),
      };

    case REVIEW_REMOVE_FROM_LIST:
      return {
        ...state,
        items: state.items.filter((item) => item.uuid !== action.payload),
        totalItems: Math.max(0, state.totalItems - 1),
      };

    case REVIEW_BULK_REMOVE_PLAYBOOKS: {
      const uuidsToRemove = new Set(action.payload as string[]);
      return {
        ...state,
        items: state.items.filter((item) => !uuidsToRemove.has(item.uuid)),
        totalItems: Math.max(0, state.totalItems - uuidsToRemove.size),
      };
    }

    // ─── Review Session ─────────────────────────────
    case REVIEW_SET_SESSION:
      return { ...state, currentSession: action.payload };

    case REVIEW_LOADING_SESSION:
      return { ...state, isLoadingSession: action.payload, error: null };

    // ─── Findings ───────────────────────────────────
    case REVIEW_SET_FINDINGS:
      return {
        ...state,
        findings: action.payload.findings,
        findingsTotal: action.payload.total,
        findingsPage: action.payload.page,
      };

    case REVIEW_APPEND_FINDINGS:
      return {
        ...state,
        findings: [...state.findings, ...action.payload.findings],
        findingsTotal: action.payload.total,
        findingsPage: action.payload.page,
      };

    case REVIEW_LOADING_FINDINGS:
      return { ...state, isLoadingFindings: action.payload, error: null };

    // ─── Sessions list ─────────────────────────────
    case REVIEW_LOADING_SESSIONS:
      return { ...state, isLoadingSessions: action.payload, error: null };

    case REVIEW_FETCH_SESSIONS:
      return {
        ...state,
        sessionItems: action.payload.items,
        sessionTotalItems: action.payload.total,
        sessionCurrentPage: action.payload.page,
        sessionTotalPages: action.payload.total_pages,
      };

    case REVIEW_APPEND_SESSIONS:
      return {
        ...state,
        sessionItems: [...state.sessionItems, ...action.payload.items],
        sessionTotalItems: action.payload.total,
        sessionCurrentPage: action.payload.page,
        sessionTotalPages: action.payload.total_pages,
      };

    case REVIEW_REMOVE_SESSION_FROM_LIST:
      return {
        ...state,
        sessionItems: state.sessionItems.filter((item) => item.uuid !== action.payload),
        sessionTotalItems: Math.max(0, state.sessionTotalItems - 1),
      };

    case REVIEW_BULK_REMOVE_SESSIONS: {
      const uuidsToRemove = new Set(action.payload as string[]);
      return {
        ...state,
        sessionItems: state.sessionItems.filter((item) => !uuidsToRemove.has(item.uuid)),
        sessionTotalItems: Math.max(0, state.sessionTotalItems - uuidsToRemove.size),
      };
    }

    case REVIEW_RENAME_SESSION_IN_LIST:
      return {
        ...state,
        sessionItems: state.sessionItems.map((item) =>
          item.uuid === action.payload.uuid ? { ...item, name: action.payload.name } : item,
        ),
      };

    case REVIEW_RESTORE_SESSION:
      return {
        ...state,
        sessionItems: state.sessionItems.map((item) =>
          item.uuid === action.payload ? { ...item, deleted_at: null } : item,
        ),
      };

    // ─── Upload state ───────────────────────────────
    case REVIEW_SET_UPLOADING:
      return { ...state, isUploading: action.payload };

    // ─── Playbook extraction ────────────────────────
    case REVIEW_EXTRACTING_PLAYBOOK:
      return { ...state, isExtractingPlaybook: action.payload };

    // ─── Error ──────────────────────────────────────
    case REVIEW_ERROR:
      return { ...state, error: action.payload };

    // ─── Real-time PubSub updates ───────────────────

    case REVIEW_FINDING_RECEIVED: {
      // Append a new finding from PubSub when viewing an active session
      const newFinding: Finding = action.payload;
      return {
        ...state,
        findings: [...state.findings, newFinding],
        findingsTotal: state.findingsTotal + 1,
      };
    }

    case REVIEW_DOCUMENT_COMPLETED: {
      // Update a document status inside the current session
      if (!state.currentSession) return state;
      const docId = action.payload.document_id;
      return {
        ...state,
        currentSession: {
          ...state.currentSession,
          documents: state.currentSession.documents.map((d) =>
            d.id === docId ? { ...d, status: 'completed' as const } : d,
          ),
        },
      };
    }

    case REVIEW_REVIEW_COMPLETED: {
      // Update session with completion data — only overwrite status if explicitly provided
      if (!state.currentSession) return state;
      return {
        ...state,
        currentSession: {
          ...state.currentSession,
          ...(action.payload.status ? { status: action.payload.status } : {}),
          overall_risk_score: action.payload.overall_risk_score ?? state.currentSession.overall_risk_score,
          findings_count: action.payload.findings_count ?? state.currentSession.findings_count,
          critical_count: action.payload.critical_count ?? state.currentSession.critical_count,
          major_count: action.payload.major_count ?? state.currentSession.major_count,
          minor_count: action.payload.minor_count ?? state.currentSession.minor_count,
          info_count: action.payload.info_count ?? state.currentSession.info_count,
        },
      };
    }

    case REVIEW_SESSION_PROGRESS: {
      // Update session with live progress from PubSub
      if (!state.currentSession) return state;
      return {
        ...state,
        currentSession: {
          ...state.currentSession,
          findings_count: action.payload.findings_count ?? state.currentSession.findings_count,
        },
      };
    }

    // ─── Playbook extraction progress (decoupled from template state) ──

    case REVIEW_EXTRACT_CONNECTING:
      return {
        ...state,
        extractionProgress: {
          ...state.extractionProgress,
          isConnecting: true,
          connected: false,
          complete: false,
          ok: false,
          summary: null,
        },
      };

    case REVIEW_EXTRACT_CONNECTED:
      return {
        ...state,
        extractionProgress: {
          ...state.extractionProgress,
          isConnecting: false,
          connected: true,
        },
      };

    case REVIEW_EXTRACT_STEPS:
      return {
        ...state,
        extractionProgress: {
          ...state.extractionProgress,
          steps: action.payload.steps,
        },
      };

    case REVIEW_EXTRACT_STEP: {
      const { id, status, message, name } = action.payload;
      const exists = state.extractionProgress.steps.some((s) => s.id === id);
      const updatedSteps = exists
        ? state.extractionProgress.steps.map((step) =>
            step.id === id ? { ...step, status, message, name: name || step.name } : step,
          )
        : [...state.extractionProgress.steps, { id, name: name || id, status, message }];
      return {
        ...state,
        extractionProgress: {
          ...state.extractionProgress,
          steps: updatedSteps,
        },
      };
    }

    case REVIEW_EXTRACT_COMPLETED:
      return {
        ...state,
        extractionProgress: {
          ...state.extractionProgress,
          complete: true,
          ok: action.payload?.ok ?? true,
          summary: action.payload?.summary ?? null,
        },
      };

    case REVIEW_EXTRACT_FAILED:
      return {
        ...state,
        extractionProgress: {
          ...state.extractionProgress,
          complete: true,
          ok: false,
          summary: action.payload?.error ?? 'Extraction failed',
        },
      };

    case REVIEW_EXTRACT_RESET:
      return {
        ...state,
        extractionProgress: initialExtractionProgress,
      };

    // ─── Collaborator management ────────────────────

    case REVIEW_SET_COLLABORATORS:
      return { ...state, collaborators: action.payload };

    case REVIEW_LOADING_COLLABORATORS:
      return { ...state, isLoadingCollaborators: action.payload };

    case REVIEW_ADD_COLLABORATOR:
      return {
        ...state,
        collaborators: [...state.collaborators, action.payload],
        currentSession: state.currentSession ? {
          ...state.currentSession,
          collaborators: [...(state.currentSession.collaborators || []), action.payload],
        } : state.currentSession,
      };

    case REVIEW_UPDATE_COLLABORATOR:
      return {
        ...state,
        collaborators: state.collaborators.map((c) =>
          c.user_id === action.payload.user_id
            ? { ...c, access: action.payload.access }
            : c,
        ),
        currentSession: state.currentSession ? {
          ...state.currentSession,
          collaborators: (state.currentSession.collaborators || []).map((c) =>
            c.user_id === action.payload.user_id
              ? { ...c, access: action.payload.access }
              : c,
          ),
        } : state.currentSession,
      };

    case REVIEW_REMOVE_COLLABORATOR:
      return {
        ...state,
        collaborators: state.collaborators.filter(
          (c) => c.user_id !== action.payload,
        ),
        currentSession: state.currentSession ? {
          ...state.currentSession,
          collaborators: (state.currentSession.collaborators || []).filter(
            (c) => c.user_id !== action.payload,
          ),
        } : state.currentSession,
      };

    // ─── Audit log ──────────────────────────────────

    case REVIEW_SET_AUDIT_LOG:
      return {
        ...state,
        auditLog: action.payload.items,
        auditLogTotal: action.payload.total,
        auditLogPage: action.payload.page,
      };

    case REVIEW_APPEND_AUDIT_LOG:
      return {
        ...state,
        auditLog: [...state.auditLog, ...action.payload.items],
        auditLogTotal: action.payload.total,
        auditLogPage: action.payload.page,
      };

    case REVIEW_LOADING_AUDIT_LOG:
      return { ...state, isLoadingAuditLog: action.payload };

    // ─── Document viewer ────────────────────────────

    case REVIEW_SET_DOCUMENT_CONTENT:
      return { ...state, documentContent: action.payload };

    case REVIEW_LOADING_DOCUMENT:
      return { ...state, isLoadingDocument: action.payload };

    // ─── Decision summary ───────────────────────────

    case REVIEW_SET_DECISION_SUMMARY:
      return { ...state, decisionSummary: action.payload };

    // ─── Update finding in-place (local decision) ───

    case REVIEW_UPDATE_FINDING: {
      const prevFinding = state.findings.find((f) => f.uuid === action.payload.uuid);
      const wasUndecided = !prevFinding?.decision;
      const isNowDecided = !!action.payload.decision;
      const isUndo = action.payload.is_undo === true;

      const updatedFindings = state.findings.map((f) => {
        if (f.uuid !== action.payload.uuid) return f;
        const patch: any = { ...f };
        if ('decision' in action.payload) patch.decision = action.payload.decision;
        if ('decision_by_name' in action.payload) {
          patch.decision_by_name = action.payload.decision_by_name ?? (isUndo ? null : f.decision_by_name);
        } else if (isUndo) {
          patch.decision_by_name = null;
        }
        if ('decision_at' in action.payload) {
          patch.decision_at = action.payload.decision_at ?? (isUndo ? null : f.decision_at);
        } else if (isUndo) {
          patch.decision_at = null;
        }
        if ('decision_notes' in action.payload) {
          patch.decision_notes = action.payload.decision_notes ?? (isUndo ? null : f.decision_notes);
        } else if (isUndo) {
          patch.decision_notes = null;
        }
        if ('modified_text' in action.payload) {
          patch.modified_text = action.payload.modified_text ?? (isUndo ? null : f.modified_text);
        } else if (isUndo) {
          patch.modified_text = null;
        }
        return patch;
      });

      // Update decision_progress on currentSession
      let updatedSession = state.currentSession;
      if (updatedSession) {
        if (wasUndecided && isNowDecided) {
          // New decision made
          updatedSession = {
            ...updatedSession,
            decision_progress: {
              ...updatedSession.decision_progress,
              decided: updatedSession.decision_progress.decided + 1,
              pending: Math.max(0, updatedSession.decision_progress.pending - 1),
            },
          };
        } else if (!wasUndecided && isUndo) {
          // Decision undone
          updatedSession = {
            ...updatedSession,
            decision_progress: {
              ...updatedSession.decision_progress,
              decided: Math.max(0, updatedSession.decision_progress.decided - 1),
              pending: updatedSession.decision_progress.pending + 1,
            },
          };
        }
      }

      return {
        ...state,
        findings: updatedFindings,
        currentSession: updatedSession,
      };
    }

    // ─── Finding notes (multi-note thread) ────

    case REVIEW_ADD_FINDING_NOTE: {
      const { findingUuid, note } = action.payload;
      return {
        ...state,
        findings: state.findings.map((f) =>
          f.uuid === findingUuid
            ? { ...f, notes: [...(f.notes || []), note] }
            : f,
        ),
      };
    }

    case REVIEW_UPDATE_FINDING_NOTE: {
      const { findingUuid, note } = action.payload;
      return {
        ...state,
        findings: state.findings.map((f) =>
          f.uuid === findingUuid
            ? {
                ...f,
                notes: (f.notes || []).map((n: any) => (n.uuid === note.uuid ? note : n)),
              }
            : f,
        ),
      };
    }

    case REVIEW_DELETE_FINDING_NOTE: {
      const { findingUuid, noteUuid } = action.payload;
      return {
        ...state,
        findings: state.findings.map((f) =>
          f.uuid === findingUuid
            ? { ...f, notes: (f.notes || []).filter((n: any) => n.uuid !== noteUuid) }
            : f,
        ),
      };
    }

    // ─── Bulk update findings (accept/reject all) ────

    case REVIEW_BULK_UPDATE_FINDINGS: {
      const { finding_uuids, decision, decision_by_name, decision_at } = action.payload;
      const uuidSet = new Set<string>(finding_uuids);

      let newlyDecided = 0;
      const bulkUpdatedFindings = state.findings.map((f) => {
        if (uuidSet.has(f.uuid)) {
          if (!f.decision) newlyDecided++;
          return {
            ...f,
            decision,
            decision_by_name: decision_by_name ?? f.decision_by_name,
            decision_at: decision_at ?? f.decision_at,
            decision_notes: null,
            modified_text: null,
          };
        }
        return f;
      });

      let bulkSession = state.currentSession;
      if (bulkSession && newlyDecided > 0) {
        bulkSession = {
          ...bulkSession,
          decision_progress: {
            ...bulkSession.decision_progress,
            decided: bulkSession.decision_progress.decided + newlyDecided,
            pending: Math.max(0, bulkSession.decision_progress.pending - newlyDecided),
          },
        };
      }

      return {
        ...state,
        findings: bulkUpdatedFindings,
        currentSession: bulkSession,
      };
    }

    // ─── Live collaboration: finding decision from another user ──

    case REVIEW_FINDING_DECISION_RECEIVED: {
      const { finding_uuid, decision, decision_by, decision_at: decAt, is_undo: remoteUndo } = action.payload;
      const prevF = state.findings.find((f) => f.uuid === finding_uuid);
      const wasUndec = !prevF?.decision;
      const isNowDec = !!decision;

      let sess = state.currentSession;
      if (sess) {
        let progressUpdate = {};
        if (wasUndec && isNowDec) {
          progressUpdate = {
            decision_progress: {
              ...sess.decision_progress,
              decided: sess.decision_progress.decided + 1,
              pending: Math.max(0, sess.decision_progress.pending - 1),
            },
          };
        } else if (!wasUndec && remoteUndo) {
          progressUpdate = {
            decision_progress: {
              ...sess.decision_progress,
              decided: Math.max(0, sess.decision_progress.decided - 1),
              pending: sess.decision_progress.pending + 1,
            },
          };
        }
        const riskUpdate = action.payload.overall_risk_score !== undefined
          ? { overall_risk_score: action.payload.overall_risk_score }
          : {};
        sess = { ...sess, ...progressUpdate, ...riskUpdate };
      }

      return {
        ...state,
        findings: state.findings.map((f) =>
          f.uuid === finding_uuid
            ? {
                ...f,
                decision,
                decision_by_name: remoteUndo ? null : decision_by,
                decision_at: remoteUndo ? null : decAt,
                modified_text: remoteUndo ? null : (action.payload.modified_text ?? f.modified_text),
              }
            : f,
        ),
        currentSession: sess,
      };
    }

    // ─── Sign-off & finalization ──────────────────────

    case REVIEW_SET_SIGN_OFF_STATUS:
      return { ...state, signOffStatus: action.payload };

    case REVIEW_LOADING_SIGN_OFF:
      return { ...state, isLoadingSignOff: action.payload };

    case REVIEW_SESSION_STATUS_CHANGE:
      return {
        ...state,
        currentSession: state.currentSession
          ? {
              ...state.currentSession,
              status: action.payload.status,
              finalized_at: action.payload.finalized_at ?? state.currentSession.finalized_at,
            }
          : null,
      };

    default:
      return state;
  }
};

export default reviewReducer;
